import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { inarixColors } from "inarix-theme";
import { Chip, IconButton, Tooltip, Menu, MenuItem } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ConstructionIcon from "@mui/icons-material/Construction";
import { useTranslation } from "react-i18next";
import { ColumnsForDataGridPro } from "../../../../redux/actions/dataTable/DataTableTypes";
import moment from "moment";
import {
  changeStatusColumn,
  localeData,
  setPinnedColumns,
} from "../../../../redux/slices/dataTable/data";
import {
  resetActionDetails,
  setSampleId,
} from "../../../../redux/slices/data/dataSampleView";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { displayCorrectValue } from "../../../data/dataSampleView/DataSampleRightSide";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ColumnStyles } from "./columnStyles";
import { selectDataTableUnifiedState } from "../../../../redux/selectors/unifiedSelectors";

import PushPinIcon from "@mui/icons-material/PushPin";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setIndividualSample: (pastaActionId: number, navigate: any) => {
      dispatch(resetActionDetails());
      dispatch(setSampleId(pastaActionId));
      if (pastaActionId) {
        navigate(`/data/${pastaActionId}`);
      }
    },

    toggleColumnVisibility: (columnField: string) => {
      dispatch(changeStatusColumn(columnField));
    },

    setPinnedColumns: (pinnedColumns: { left: string[]; right: string[] }) => {
      dispatch(setPinnedColumns(pinnedColumns));
    },
  };
};

const CustomColumnHeader = ({
  headerName,
  field,
  allColumns,
  onToggleVisibility,
  setPinnedColumns,
  pinnedColumns,
  columnType,
}: {
  headerName: string;
  field: string;
  allColumns: any[];
  onToggleVisibility: (field: string) => void;
  setPinnedColumns: (pinnedColumns: {
    left: string[];
    right: string[];
  }) => void;
  pinnedColumns: {
    left: string[];
    right: string[];
  };
  columnType?: string;
}) => {
  const [t] = useTranslation("global");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getColumnTypeDescription = (type?: string) => {
    switch (type) {
      case "measure":
        return t("Data.SwitchesValues.measure");
      case "exported-measure":
        return t("Data.SwitchesValues.measure");
      case "label":
        return t("Data.SwitchesValues.label");
      case "data":
        return t("Data.SwitchesValues.data");
      default:
        return "";
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (columnField: string) => {
    onToggleVisibility(columnField);
    handleClose();
  };

  const handleToggleFreeze = (
    columnField: string,
    pinned: "left" | "right"
  ) => {
    if (pinned === "left") {
      if (pinnedColumns.left.includes(columnField)) {
        setPinnedColumns({
          ...pinnedColumns,
          left: pinnedColumns.left.filter((col) => col !== columnField),
        });
        return;
      }

      setPinnedColumns({
        ...pinnedColumns,
        left: [...pinnedColumns.left, columnField],
        right: pinnedColumns.right.filter((col) => col !== columnField),
      });
    } else {
      if (pinnedColumns.right.includes(columnField)) {
        setPinnedColumns({
          ...pinnedColumns,
          right: pinnedColumns.right.filter((col) => col !== columnField),
        });
        return;
      }
      setPinnedColumns({
        ...pinnedColumns,
        right: [...pinnedColumns.right, columnField],
        left: pinnedColumns.left.filter((col) => col !== columnField),
      });
    }
  };

  return (
    <div style={ColumnStyles.headerContainer}>
      <Tooltip title={getColumnTypeDescription(columnType)} arrow>
        <span style={ColumnStyles.headerText}>{headerName}</span>
      </Tooltip>
      <button
        type="button"
        aria-label="Column options"
        onClick={handleClick}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        style={ColumnStyles.headerMenuButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        onFocus={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <MoreVertIcon fontSize="small" />
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleToggleColumn(field)} dense>
          <VisibilityIcon
            sx={{
              marginRight: 1,
              color: "primary.main",
              fontSize: "1.2rem",
            }}
          />
          <span> {t("Data.Hide")}</span>
        </MenuItem>

        <MenuItem onClick={() => handleToggleFreeze(field, "left")} dense>
          <PushPinIcon
            sx={{
              marginRight: 1,
              color: pinnedColumns.left.includes(field)
                ? "primary.main"
                : "text.disabled",
              fontSize: "1.2rem",
              transform: "rotate(-45deg)",
            }}
          />
          {pinnedColumns.left.includes(field)
            ? t("Data.unpinLeft")
            : t("Data.pinLeft")}
        </MenuItem>
        <MenuItem onClick={() => handleToggleFreeze(field, "right")} dense>
          <PushPinIcon
            sx={{
              marginRight: 1,
              color: pinnedColumns.right.includes(field)
                ? "primary.main"
                : "text.disabled",
              fontSize: "1.2rem",
              transform: "rotate(45deg)",
            }}
          />
          {pinnedColumns.right.includes(field)
            ? t("Data.unpinRight")
            : t("Data.pinRight")}
        </MenuItem>
      </Menu>
    </div>
  );
};

const DataTableColumns = () => {
  const [t] = useTranslation("global");

  const { columns, actions, pinnedColumns } = useSelector(
    selectDataTableUnifiedState
  );

  const { setIndividualSample, toggleColumnVisibility, setPinnedColumns } =
    SetActions();

  const navigate = useNavigate();
  const locale = useSelector(localeData);

  let columnsDataTable: ColumnsForDataGridPro[] | undefined = columns?.map(
    (column) => {
      if (column.field === "id") {
        return {
          headerName: t("Data.table.Details"),
          field: "id",
          width: 120,
          sortable: false,
          type: "actions",
          show: column.show,
          disableColumnMenu: true,
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          foregroundColor: column.foregroundColor,
          backgroundColor: column.backgroundColor,
          renderHeader: () => (
            <CustomColumnHeader
              headerName={t("Data.table.Details")}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
            />
          ),
          renderCell: (params: GridRenderCellParams) => {
            const chipIdDevice = () => {
              const idConsole = () => {
                setIndividualSample(params.row.details as number, navigate);
              };
              return (
                <div
                  style={{
                    ...ColumnStyles.detailsContainer,
                    borderLeftColor: inarixColors.divider_inarix,
                    borderRightColor: inarixColors.divider_inarix,
                  }}
                >
                  <Tooltip
                    title={t("Fleet_actions.table.Details" as string)}
                    arrow
                  >
                    <IconButton
                      sx={{
                        ...ColumnStyles.detailsButton,
                        "&:hover": {
                          color: inarixColors.light_inarix,
                        },
                      }}
                      onClick={idConsole}
                      aria-label="delete"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            };
            return chipIdDevice();
          },
        };
      }

      if (column.field === "status") {
        return {
          headerName: t("Data.table.Status"),
          field: "status",
          width: column.width,
          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          filterable: false,
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          foregroundColor: column.foregroundColor,
          backgroundColor: column.backgroundColor,
          renderHeader: () => (
            <CustomColumnHeader
              headerName={t("Data.table.Status")}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
            />
          ),

          renderCell: (params: GridRenderCellParams) => {
            const value = params.value;

            const statusConfig = {
              completed: {
                chipColor: "success" as const,
                label: t("Data.table.status_columns.success") || "Completed",
                styles: ColumnStyles.statusChips.completed,
                icon: <CheckCircleIcon sx={ColumnStyles.statusIcon} />,
              },
              failed: {
                chipColor: "error" as const,
                label: t("Data.table.status_columns.failed") || "Failed",
                styles: ColumnStyles.statusChips.failed,
                icon: <ErrorIcon sx={ColumnStyles.statusIcon} />,
              },
              pending: {
                chipColor: "warning" as const,
                label: t("Data.table.status_columns.pending") || "Pending",
                styles: ColumnStyles.statusChips.pending,
                icon: <HourglassEmptyIcon sx={ColumnStyles.statusIcon} />,
              },
              processing: {
                chipColor: "info" as const,
                label:
                  t("Data.table.status_columns.in_progress") || "In progress",
                styles: ColumnStyles.statusChips.processing,
                icon: <SettingsIcon sx={ColumnStyles.statusIcon} />,
              },
              inDevelopment: {
                chipColor: "secondary" as const,
                label:
                  t("Data.table.status_columns.in_development") ||
                  "En desarrollo",
                styles: ColumnStyles.statusChips.inDevelopment,
                icon: <ConstructionIcon sx={ColumnStyles.statusIcon} />,
              },
              default: {
                chipColor: "default" as const,
                label: t("Data.table.status_columns.default") || "Unknown",
                styles: ColumnStyles.statusChips.default,
                icon: <HelpOutlineIcon sx={ColumnStyles.statusIcon} />,
              },
            };

            const config =
              statusConfig[value as keyof typeof statusConfig] ||
              statusConfig.default;

            const tooltipDescription = () => {
              switch (value) {
                case "completed":
                  return t("Data.table.status_columns_desc.success");
                case "failed":
                  return t("Data.table.status_columns_desc.failed");
                case "pending":
                  return t("Data.table.status_columns_desc.pending");
                case "processing":
                  return t("Data.table.status_columns_desc.in_progress");
                case "inDevelopment":
                  return t("Data.table.status_columns_desc.in_development");
                default:
                  return t("Data.table.status_columns_desc.default");
              }
            };

            return (
              <div>
                <Tooltip title={tooltipDescription()} arrow>
                  <Chip
                    label={
                      <div style={ColumnStyles.chipContainer}>
                        {config.icon}
                        {config.label}
                      </div>
                    }
                    color={config.chipColor}
                    size="small"
                    variant="outlined"
                    sx={{
                      ...ColumnStyles.statusChip,
                      backgroundColor: config.styles.backgroundColor,
                      color: config.styles.color,
                      borderColor: config.styles.borderColor,
                    }}
                  />
                </Tooltip>
              </div>
            );
          },
        };
      }

      if (column.field === "cropTextId") {
        return {
          field: column.field,
          headerName: column.headerName,
          width: column.width,
          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          filterable: false,
          columnType: column.columnType,
          useShortResult: column.useShortResult,
          renderHeader: () => (
            <CustomColumnHeader
              headerName={column.headerName}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
            />
          ),
          renderCell: (params: GridRenderCellParams) => {
            const colors = params?.row?.colors;
            const backgroundColor = colors?.iconBackgroundHexColor;
            const foregroundColor = colors?.iconForegroundHexColor;
            const value = params.value;

            return (
              <div>
                <Chip
                  label={value ? value : ""}
                  sx={{
                    ...ColumnStyles.cropChip,
                    backgroundColor: backgroundColor,
                    color: foregroundColor,
                  }}
                />
              </div>
            );
          },
        };
      }

      if (column.columnType === "measure") {
        return {
          field: column.field,
          headerName: column.headerName,
          width: column.width,
          sortable: false,
          show: column.show,
          disableColumnMenu: true,
          filterable: false,
          columnType: column.columnType,
          height: 500,
          useShortResult: column.useShortResult,
          renderHeader: () => (
            <CustomColumnHeader
              headerName={column.headerName}
              field={column.field}
              allColumns={columns}
              onToggleVisibility={toggleColumnVisibility}
              setPinnedColumns={setPinnedColumns}
              pinnedColumns={pinnedColumns}
              columnType={column.columnType}
            />
          ),
          renderCell: (params: GridRenderCellParams) => {
            const values = params.value;

            if (column.useShortResult) {
              return <div>{values.shortResult}</div>;
            } else {
              return (
                <div className="measure-container-data-table">
                  {values !== undefined
                    ? values?.detailedResult?.length > 0
                      ? values?.detailedResult?.map((v) => (
                          <div key={`${v?.title}`}>
                            <div className="">{v?.title}</div>
                            <div className="">{displayCorrectValue(v)}</div>
                          </div>
                        ))
                      : values.shortResult
                    : ""}
                </div>
              );
            }
          },
        };
      }

      return {
        field: column.field,
        headerName: column.headerName,
        width: column.width,
        sortable: false,
        show: column.show,
        disableColumnMenu: true,
        filterable: false,
        columnType: column.columnType,
        useShortResult: column.useShortResult,
        foregroundColor: column.foregroundColor,
        backgroundColor: column.backgroundColor,
        renderHeader: () => (
          <CustomColumnHeader
            headerName={column.headerName}
            field={column.field}
            allColumns={columns}
            onToggleVisibility={toggleColumnVisibility}
            setPinnedColumns={setPinnedColumns}
            pinnedColumns={pinnedColumns}
            columnType={column.columnType}
          />
        ),
      };
    }
  ) as any[];

  let actionsDataTable = actions?.map((action: any) => {
    const createdAt = moment(action?.createdAt);
    createdAt.locale(locale);
    const createdAtModify = createdAt.format("l, LT");

    const device = () => {
      if (!action.deviceName) {
        return t("Data.table.No_device");
      }
      if (!action.deviceName) {
        return t("Fleet_users_devices.table.Empty_device");
      } else {
        return action.deviceName;
      }
    };

    const location = () => {
      if (!action.geoPointName) {
        return t("Data.table.No_geo_point");
      } else {
        return action.geoPointName;
      }
    };

    const exportedMeasure = action?.aggExportedPredictions ?? {};
    const measures = action?.aggPayloads ?? {};
    const labels = action?.labels ?? {};

    // build row generically based on fetched columns
    const row =
      columnsDataTable?.reduce((obj: any, col) => {
        if (col.field === "orgDeviceId") {
          obj[col.field] = device();
        } else if (col.field === "cropTextId") {
          obj[col.field] = action.cropTextId
            ? action.cropTextId.text
            : t("Data.table.No_crop");
        } else if (col.field === "scenarioNameTextId") {
          obj[col.field] = action.scenarioNameTextId
            ? action.scenarioNameTextId.text
            : "";
        } else if (col.field === "geoPointName") {
          obj[col.field] = location();
        } else if (col.field === "createdAt") {
          obj[col.field] = createdAtModify;
        } else if (col.field === "actionId") {
          // do nothing
        } else if (col.columnType === "exported-measure") {
          obj[col.field] = exportedMeasure[col.field];
        } else if (col.columnType === "label") {
          obj[col.field] = labels[col.field];
        } else if (col.columnType === "measure") {
          obj[col.field] = measures[col.field]?.predictionsV2;
        } else {
          obj[col.field] = action[col.field];
        }

        return obj;
      }, {}) || {};

    row["colors"] = action.cropInfoColor;

    row["id"] = action?.pastActionId;
    row["details"] = action.pastActionId;
    row["status"] = "inDevelopment";

    return row;
  });

  if (columnsDataTable?.length === 0 || !columnsDataTable) {
    columnsDataTable = [];
  }

  const columnSwitchesValues =
    columnsDataTable[0] !== undefined
      ? columnsDataTable?.map((col: any) => ({
          name: col.headerName ?? col.field,
          id: col.field,
          isSelected: col.show,
        }))
      : [
          {
            name: "",
            id: "",
            isSelected: false,
          },
        ];

  return {
    columnsDataTable,
    columnSwitchesValues,
    actionsDataTable,
  };
};

export default DataTableColumns;
